import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ChevronDown, Signal, Wifi, BatteryFull } from "lucide-react";

// Renders children at a fixed design size and scales them to the container width.
export const Scaled = ({ w, h, children }) => {
  const ref = useRef(null);
  const [s, setS] = useState(0);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setS(e.contentRect.width / w));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [w]);
  return (
    <div ref={ref} className="relative w-full overflow-hidden" style={{ height: h * s }}>
      <div className="absolute left-0 top-0 origin-top-left" style={{ width: w, height: h, transform: `scale(${s})` }}>{children}</div>
    </div>
  );
};

export const BrowserFrame = ({ children, title = "Cross Jobs · Supervisor dashboard", testid }) => (
  <div className="rounded-xl overflow-hidden border border-white/10 bg-[#0F172A] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.75)]" data-testid={testid}>
    <div className="flex items-center gap-1.5 px-3 h-8 bg-[#1E293B] border-b border-white/5">
      {["#F87171", "#FBBF24", "#34D399"].map((c) => <span key={c} className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />)}
      <span className="ml-3 flex-1 truncate rounded-md bg-white/5 px-2 py-0.5 text-[10px] font-mono text-slate-400">{title}</span>
    </div>
    <Scaled w={760} h={470}>{children}</Scaled>
  </div>
);

export const PhoneFrame = ({ children, className = "", testid }) => (
  <div className={`rounded-[2.2rem] bg-[#0F172A] p-2 border border-white/15 shadow-[0_40px_80px_-25px_rgba(0,0,0,0.7)] ${className}`} data-testid={testid}>
    <div className="relative rounded-[1.75rem] overflow-hidden bg-white">
      <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-[28%] h-[2.2%] min-h-[10px] rounded-full bg-[#0F172A] z-20" />
      <Scaled w={300} h={620}>{children}</Scaled>
    </div>
  </div>
);

// Orange focus ring + pulse that marks what each walkthrough step is about.
export const Spot = ({ on, children, className = "" }) => (
  <div className={`relative ${className}`}>
    {children}
    {on && (
      <>
        <motion.span initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.25 }}
          className="pointer-events-none absolute -inset-1.5 rounded-[10px] ring-2 ring-[#EA580C] shadow-[0_0_0_6px_rgba(234,88,12,0.16)]" />
        <span className="pointer-events-none absolute -top-2 -right-2 w-3.5 h-3.5">
          <span className="absolute inset-0 rounded-full bg-[#EA580C] animate-ping" />
          <span className="absolute inset-0 rounded-full bg-[#EA580C] border-2 border-white" />
        </span>
      </>
    )}
  </div>
);

export const Typed = ({ text, delay = 0.3 }) => (
  <span>{text.split("").map((c, i) => (
    <motion.span key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: delay + i * 0.06 }}>{c}</motion.span>
  ))}</span>
);

export const DepotPill = ({ name }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold">
    <MapPin className="w-3 h-3 text-[#EA580C]" />{name}<ChevronDown className="w-3 h-3 text-slate-400" />
  </span>
);

const STATUS = {
  "On site": "bg-emerald-50 text-emerald-700 [&>i]:bg-emerald-500 [&>i]:animate-pulse",
  "In progress": "bg-amber-50 text-amber-700 [&>i]:bg-amber-500",
  "Not started": "bg-slate-100 text-slate-600 [&>i]:bg-slate-400",
};
export const JobStatus = ({ s }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS[s]}`}><i className="w-1.5 h-1.5 rounded-full" />{s}</span>
);

const TABS = ["Overview", "Jobs", "Invoices", "Roster", "Timesheets", "Crew", "Depots"];
export const MockShell = ({ tab, depot = "North Yard", depotSlot, children }) => (
  <div className="w-full h-full bg-[#F7F5F1] text-[#0F172A] flex flex-col text-[12px] text-left">
    <div className="h-12 shrink-0 bg-white border-b border-slate-200 flex items-center px-4 gap-2.5">
      <img src="/crossjobs-mark.png" className="h-7 w-auto" alt="" />
      <span className="font-display font-bold text-[14px]">Southern Cross Plastering</span>
      <div className="ml-auto">{depotSlot || <DepotPill name={depot} />}</div>
      <span className="w-7 h-7 rounded-full bg-[#1E3A5F] text-white text-[10px] font-bold grid place-items-center">JM</span>
    </div>
    <div className="px-4 pt-3 flex gap-1 shrink-0">
      {TABS.map((t) => (
        <span key={t} className={`px-3 py-1.5 rounded-full text-[11px] font-semibold ${t === tab ? "bg-[#1E3A5F] text-white" : "text-slate-500"}`}>{t}</span>
      ))}
    </div>
    <div className="flex-1 p-4 relative">{children}</div>
  </div>
);

export const PhoneShell = ({ title, children }) => (
  <div className="w-full h-full bg-[#F7F5F1] text-[#0F172A] flex flex-col text-[13px] text-left">
    <div className="h-9 shrink-0 px-6 flex items-center justify-between text-[11px] font-semibold">
      <span className="font-mono">7:02</span>
      <span className="flex gap-1 items-center"><Signal className="w-3 h-3" /><Wifi className="w-3 h-3" /><BatteryFull className="w-4 h-4" /></span>
    </div>
    <div className="h-12 shrink-0 px-4 flex items-center gap-2 bg-white border-b border-slate-200">
      <img src="/crossjobs-mark.png" className="h-7 w-auto" alt="" />
      <span className="font-display font-bold text-[13px]">Southern Cross Plastering</span>
    </div>
    {title && <p className="px-4 pt-3 font-display font-bold text-[17px]">{title}</p>}
    <div className="flex-1 p-4 relative">{children}</div>
  </div>
);
